'use client';

import { cva } from 'class-variance-authority';
import Image from 'next/image';
import { CuteSpinner } from '@/components/common/CuteSpinner';
import { Avatar } from '@/components/ui/avatar';
import { ModelTelemetryTooltip } from '@/components/ui/model-telemetry-tooltip';
import { useAuth } from '@/hooks/useAuth';
import { useRelativeTime } from '@/hooks/useRelativeTime';
import { toArray } from '@/lib/utils/to-array';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import type { Message } from './chat-types';
import type { SuggestionSelectHandler } from './interface/types';
import { MessageParser } from './MessageParser';

interface MessageBubbleProperties {
  layout?: 'floating' | 'sidebar';
  message: Message;
  onSuggestionSelect?: SuggestionSelectHandler | undefined;
  powerUserEnabled?: boolean;
}

type SenderVariant = 'user' | 'assistant';

const wrapperVariants = cva('flex min-w-0 gap-3', {
  variants: {
    sender: {
      user: 'flex-row-reverse',
      assistant: 'flex-row',
    },
  },
  defaultVariants: {
    sender: 'assistant',
  },
});

const columnVariants = cva('flex min-w-0 flex-col gap-2', {
  variants: {
    sender: {
      user: 'items-end text-right',
      assistant: 'items-start text-left',
    },
  },
  defaultVariants: {
    sender: 'assistant',
  },
});

const bubbleVariants = cva(
  'min-w-0 break-words [overflow-wrap:anywhere] rounded-3xl px-5 py-4 text-sm transition-all duration-200',
  {
    variants: {
      sender: {
        user: 'chat-bubble-user ml-auto',
        assistant: 'chat-bubble-assistant',
      },
      layout: {
        sidebar: 'w-full max-w-full',
        floating: 'w-full max-w-full sm:w-fit sm:max-w-[min(90ch,90%)]',
      },
      streaming: {
        true: '',
        false: '',
      },
    },
    defaultVariants: {
      sender: 'assistant',
      layout: 'floating',
      streaming: false,
    },
    compoundVariants: [
      {
        sender: 'assistant',
        streaming: true,
        class: 'outline outline-1 outline-primary/30 shadow-[020px_48px_-34px_var(--mix-hslprimary-62pct-transparent)]',
      },
    ],
  }
);

const timestampVariants = cva('chat-timestamp flex items-center gap-1.5 text-xs font-medium opacity-80', {
  variants: {
    sender: {
      user: 'chat-timestamp-user justify-end',
      assistant: 'text-muted-foreground',
    },
  },
  defaultVariants: {
    sender: 'assistant',
  },
});

export function MessageBubble({
  message,
  layout = 'floating',
  powerUserEnabled = false,
  onSuggestionSelect,
}: MessageBubbleProperties) {
  const { user: currentUser } = useAuth();
  const isUser = message.role === 'user';
  const sender: SenderVariant = isUser ? 'user' : 'assistant';
  const isStreaming = message.role === 'assistant' && message.status === 'streaming';
  const isSidebarLayout = layout === 'sidebar';
  const sanitizedContent = toTrimmedNonEmptyString(message.content) ?? '';
  const showThinkingPlaceholder = isStreaming && sanitizedContent.length === 0;
  const toolInvocations = toArray(message.toolInvocations);
  const hasToolInvocations = toolInvocations.length > 0;
  const hasToolResults = hasToolInvocations && toolInvocations.some((invocation) => invocation.state === 'result');
  const authUser = currentUser ?? null;
  const userAvatarLabel =
    toTrimmedNonEmptyString(authUser?.name) ?? toTrimmedNonEmptyString(authUser?.twitterUsername) ?? 'You';
  const userAvatarSource = toTrimmedNonEmptyString(authUser?.image) ?? undefined;

  const telemetry = message.telemetry;
  const usageSummary = telemetry?.usage;
  const costSummary = telemetry?.cost;
  const showTelemetryTooltip = !isUser && powerUserEnabled && !isStreaming;
  const timestampDate = message.timestamp;
  const absoluteTimeLabel = Number.isNaN(timestampDate.getTime())
    ? null
    : timestampDate.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
  const relativeTime = useRelativeTime(timestampDate, { style: 'short' });
  const timestampIso = Number.isNaN(timestampDate.getTime()) ? null : timestampDate.toISOString();
  const relativeLabel = toTrimmedNonEmptyString(relativeTime.label);
  const timestampLabel = relativeTime.isValid && relativeLabel ? relativeLabel : (absoluteTimeLabel ?? '—');
  const shouldHideAssistantBubble =
    !(isUser || showThinkingPlaceholder) && sanitizedContent.length === 0 && hasToolResults;
  const isHidden = message.visibility === 'hidden';

  if (isHidden || shouldHideAssistantBubble) {
    return null;
  }

  return (
    <div className={wrapperVariants({ sender })}>
      <div className="flex-shrink-0">
        {isUser ? (
          <Avatar
            src={userAvatarSource}
            alt={userAvatarLabel}
            size={32}
            className="h-8 w-8 ring-1 ring-primary/20"
            fallbackText={userAvatarLabel}
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-border/50 bg-background shadow-sm">
            <Image src="/maskable-icon-512x512.png" alt="Sweetistics" width={32} height={32} className="h-8 w-8" />
          </div>
        )}
      </div>

      <div className={columnVariants({ sender })}>
        {isStreaming && showThinkingPlaceholder ? (
          <ThinkingPlaceholder />
        ) : (
          <div
            className={bubbleVariants({
              sender,
              layout: isSidebarLayout ? 'sidebar' : 'floating',
              streaming: isStreaming,
            })}
          >
            <div className="max-w-none break-words [overflow-wrap:anywhere] text-[15px] leading-relaxed">
              <MessageParser content={message.content} onSuggestionSelect={onSuggestionSelect} />
            </div>
            {isStreaming ? (
              <div className="mt-3 flex items-center gap-2 text-xs font-medium text-primary">
                <CuteSpinner size={14} className="border-current/30 border-t-current" />
                Crafting a response…
              </div>
            ) : null}
          </div>
        )}
        {isStreaming ? null : (
          <div className={timestampVariants({ sender })} suppressHydrationWarning>
            {showTelemetryTooltip ? (
              <ModelTelemetryTooltip
                usage={usageSummary ?? undefined}
                cost={costSummary ?? undefined}
                className="inline-flex cursor-help border-b border-dotted border-muted-foreground/60 bg-transparent p-0 text-left align-baseline outline-none focus-visible:ring-1 focus-visible:ring-primary/50"
                tooltipClassName="max-w-xs space-y-1 text-xs"
              >
                <time
                  className="tabular-nums whitespace-nowrap"
                  title={absoluteTimeLabel ?? undefined}
                  dateTime={timestampIso ?? undefined}
                >
                  {timestampLabel}
                </time>
              </ModelTelemetryTooltip>
            ) : (
              <time
                className="tabular-nums whitespace-nowrap"
                title={absoluteTimeLabel ?? undefined}
                dateTime={timestampIso ?? undefined}
              >
                {timestampLabel}
              </time>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ThinkingPlaceholder() {
  return (
    <div className="flex items-center justify-center">
      <span className="sr-only">Generating response</span>
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
        <CuteSpinner size={16} className="text-primary border-primary/30 border-t-primary" aria-hidden="true" />
      </span>
    </div>
  );
}
